import React from 'react';
import PropTypes from 'prop-types';
import {
    InputAdornment,
    Input,
    InputLabel,
    FormHelperText,
    Typography,
} from '@material-ui/core';
import { fieldPropTypes } from 'redux-form';
import { debounce } from 'lodash/function';
import RequiredAsterisk from '%app/js/common/components/material/TextField/component/RequiredAsterisk';

export default class TextField extends React.PureComponent {
    constructor(props) {
        super(props);

        const { input: { value } } = props;

        this.state = {
            value,
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.changeValue = debounce(this.doChangeValue, 200);
    }

    componentDidUpdate(prevProps) {
        const { input: { value: propValue } } = this.props;
        const { input: { value: prevPropValue } } = prevProps;
        if (propValue !== prevPropValue) {
            this.setState({
                value: propValue,
            });
        }
    }

    handleChange({ target: { value } }) {
        this.setState({ value: this.clearEmptyString(value) });
        this.changeValue(value);
    }

    handleBlur({ target: { value } }) {
        const { input: { onBlur } } = this.props;
        onBlur(this.clearEmptyString(value));
    }

    doChangeValue(value) {
        const { input: { onChange } } = this.props;
        onChange(this.clearEmptyString(value));
    }

    clearEmptyString(value) {
        return value === '' ? null : value;
    }

    render() {
        const {
            id,
            endAdornments,
            startAdornments,
            type,
            disabled,
            label,
            meta: { error },
            placeholder,
            errors,
            labelProps,
            multiline,
            required,
            disclaimer,
            classes,
            adornmentProps,
            inputComponent,
            inputProps,
            ...rest
        } = this.props;

        const { value } = this.state;

        const hasErrors = Boolean(error || errors);

        return (
            <React.Fragment>
                <InputLabel
                    {...labelProps}
                    shrink={!(value === undefined || value === null)}
                    style={{ width: '100%' }}
                >
                    { label }
                    <RequiredAsterisk required={required} />
                </InputLabel>
                <Input
                    {...rest}
                    id={id}
                    value={(value === undefined || value === null) ? '' : value}
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                    disabled={disabled}
                    fullWidth
                    type={type}
                    inputComponent={inputComponent}
                    inputProps={inputProps}
                    error={hasErrors}
                    placeholder={placeholder}
                    endAdornment={endAdornments
                        ? (
                            <InputAdornment position="end" {...adornmentProps}>
                                { endAdornments }
                            </InputAdornment>
                    ) : ''}
                    startAdornment={startAdornments
                        ? (
                            <InputAdornment position="start" {...adornmentProps}>
                                { startAdornments }
                            </InputAdornment>
                        ) : ''}
                    multiline={multiline}
                />
                {disclaimer !== null && (
                    <FormHelperText component="div" className={classes.formHelperText}>
                        <Typography variant="caption" className={classes.disclaimer}>
                            { disclaimer }
                        </Typography>
                    </FormHelperText>
                )}
            </React.Fragment>
        );
    }
}

TextField.propTypes = {
    endAdornments: PropTypes.any,
    startAdornments: PropTypes.any,
    type: PropTypes.string,
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    multiline: PropTypes.bool,
    errors: PropTypes.any,
    labelProps: PropTypes.object,
    required: PropTypes.bool,
    disclaimer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    id: PropTypes.string,
    classes: PropTypes.shape({
        formHelperText: PropTypes.string,
        disclaimer: PropTypes.string,
    }).isRequired,
    adornmentProps: PropTypes.object,
    inputComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    inputProps: PropTypes.object,
    input: PropTypes.shape({
        onBlur: fieldPropTypes.input.onBlur,
        onChange: fieldPropTypes.input.onChange,
        value: fieldPropTypes.input.value,
    }).isRequired,
    meta: PropTypes.shape({
        error: fieldPropTypes.meta.error,
    }).isRequired,
};
TextField.defaultProps = {
    labelProps: null,
    endAdornments: null,
    startAdornments: null,
    type: 'text',
    disabled: false,
    errors: null,
    multiline: false,
    required: false,
    disclaimer: null,
    id: null,
    adornmentProps: null,
    inputComponent: Input,
    inputProps: {},
};
